import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Plus, ChevronRight, Compass, Lock } from 'lucide-react';
import api from '../lib/api';
import CommunityCreateSheet from '../components/CommunityCreateSheet';
import CommunityLockedSheet from '../components/CommunityLockedSheet';

export default function Communities() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('mine');
  const [mine, setMine] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showLocked, setShowLocked] = useState(false);
  const [eligibility, setEligibility] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, d, e] = await Promise.all([
        api.get('/communities/mine').catch(() => ({ data: [] })),
        api.get('/communities/discover/list?limit=30').catch(() => ({ data: [] })),
        api.get('/communities/eligibility').catch(() => ({ data: null })),
      ]);
      setMine(m.data || []);
      setDiscover(d.data || []);
      setEligibility(e.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const tryCreate = () => {
    if (eligibility?.eligible) setShowCreate(true);
    else setShowLocked(true);
  };

  return (
    <div className="content-area pb-12" data-testid="communities-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button data-testid="communities-back" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} className="text-[#111]" />
        </button>
        <h1 className="text-lg font-bold tracking-tight text-[#111111] flex-1">Communities</h1>
        <button
          data-testid="communities-create-cta"
          onClick={tryCreate}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
            eligibility?.eligible ? 'bg-[#B6E600] text-[#111]' : 'bg-[#F1F0EA] text-[#6B6E73]'
          }`}
        >
          {eligibility?.eligible
            ? <><Plus size={14} /> New</>
            : <><Lock size={12} strokeWidth={2.8} /> Locked</>}
        </button>
      </div>

      <div className="px-4">
        <div className="flex gap-1.5 mb-4">
          <button
            data-testid="communities-tab-mine"
            onClick={() => setTab('mine')}
            className={`flex-1 py-2 rounded-full text-xs font-bold ${tab === 'mine' ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73]'}`}
          >
            My communities
          </button>
          <button
            data-testid="communities-tab-discover"
            onClick={() => setTab('discover')}
            className={`flex-1 py-2 rounded-full text-xs font-bold ${tab === 'discover' ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73]'}`}
          >
            Discover
          </button>
        </div>

        {loading ? (
          <p className="text-center text-sm text-[#9B9EA3] py-8">Loading…</p>
        ) : tab === 'mine' ? (
          mine.length === 0 ? (
            <EmptyState
              icon={<Users size={22} />}
              title="No communities yet"
              body="Start a community to keep your group alive between events."
              cta="Start one"
              onCta={tryCreate}
            />
          ) : (
            <List items={mine} onTap={(c) => navigate(`/community/${c.id}`)} testIdPrefix="my" />
          )
        ) : (
          discover.length === 0 ? (
            <EmptyState
              icon={<Compass size={22} />}
              title="No communities here yet"
              body="Be the first to start a community in your city."
              cta="Start one"
              onCta={tryCreate}
            />
          ) : (
            <List items={discover} onTap={(c) => navigate(`/community/${c.id}`)} testIdPrefix="discover" />
          )
        )}
      </div>

      <CommunityCreateSheet
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(c) => { setShowCreate(false); navigate(`/community/${c.id}`); }}
      />
      <CommunityLockedSheet open={showLocked} onClose={() => setShowLocked(false)} />
    </div>
  );
}

function List({ items, onTap, testIdPrefix }) {
  return (
    <div className="space-y-2">
      {items.map(c => (
        <button
          key={c.id}
          data-testid={`community-row-${testIdPrefix}-${c.id}`}
          onClick={() => onTap(c)}
          className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-4 active:bg-[#F1F0EA]"
        >
          {c.cover_image ? (
            <img src={c.cover_image} alt="" className="w-12 h-12 rounded-2xl object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
              <Users size={20} />
            </div>
          )}
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-extrabold text-[#111] truncate">{c.name}</p>
            <p className="text-xs text-[#6B6E73] truncate">
              {c.member_count || 0} members · {c.city || 'Anywhere'}
              {c.is_host && ' · Host'}
            </p>
          </div>
          <ChevronRight size={18} className="text-[#9B9EA3]" />
        </button>
      ))}
    </div>
  );
}

function EmptyState({ icon, title, body, cta, onCta }) {
  return (
    <div data-testid="communities-empty" className="bg-white rounded-3xl border border-black/[0.06] p-6 text-center">
      <div className="w-12 h-12 mx-auto rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center mb-3">
        {icon}
      </div>
      <h2 className="text-base font-extrabold text-[#111]">{title}</h2>
      <p className="text-xs text-[#6B6E73] mt-1 leading-relaxed">{body}</p>
      {cta && (
        <button
          data-testid="communities-empty-cta"
          onClick={onCta}
          className="mt-4 w-full bg-[#B6E600] text-[#111] font-bold rounded-full py-2.5 text-sm"
        >
          {cta}
        </button>
      )}
    </div>
  );
}
